import { useEffect } from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import {
	RootCauseListPayload,
	RootCauseWidgetTransportQuantityDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../../hooks/useResource";
import { CardDelta } from "../../components/CardDelta";
import DoubleHorizontalBarChart from "./DoubleHorizontalBarChart";

type TransportQuantityProps = {
	onWarningsChange?: (warnings?: string[]) => void;
};

export const TransportQuantity = ({ onWarningsChange }: TransportQuantityProps) => {
	const { rootCauseState } = useFiltersRegistry();
	const { t } = useTranslation("dataInsights.rootcause");

	const { data, isLoading } = useReadResource<RootCauseListPayload, RootCauseWidgetTransportQuantityDTO>(
		{
			route: ApiRoutes.RootCauseWidjetTransportQuantity,
		},
		{
			readMethod: "post",
		},
		{ ...rootCauseState.payload }
	);

	useEffect(() => {
		onWarningsChange?.(data?.warnings ?? []);
	}, [data?.warnings]);

	const chartData = [
		{
			id: "weight",
			label: t("root cause results/transported weight and mileage/weight"),
			increase: data?.co2IncreaseExplainedByWeight?.value ?? 0,
			decrease: data?.co2DecreaseExplainedByWeight?.value ?? 0,
		},
		{
			id: "distance",
			label: t("root cause results/transported weight and mileage/distance"),
			increase: data?.co2IncreaseExplainedByDistance?.value ?? 0,
			decrease: data?.co2DecreaseExplainedByDistance?.value ?? 0,
		},
	];

	const deltaValues = [
		{
			title: t("root cause results/transported weight and mileage/delta because of tkm"),
			value: data?.co2DeltaExplainedByTkm.value ?? 0,
			measure: data?.co2DeltaExplainedByTkm.measure ?? "-",
		},
		{
			title: t("root cause results/transported weight and mileage/increase because of tkm"),
			value: data?.co2IncreaseExplainedByTkm.value ?? 0,
			measure: data?.co2IncreaseExplainedByTkm.measure ?? "-",
		},
		{
			title: t("root cause results/transported weight and mileage/decrease because of tkm"),
			value: data?.co2DecreaseExplainedByTkm.value ?? 0,
			measure: data?.co2DecreaseExplainedByTkm.measure ?? "-",
		},
	];

	return (
		<Stack gap={2}>
			<Typography variant="body2">{t("root cause results/transport quantity sub")}</Typography>

			<Stack direction={"row"} spacing={2}>
				<CardDelta values={deltaValues} isLoading={isLoading} />
				<CardDelta
					title={t("root cause results/transported weight and mileage/tonne-km delta")}
					value={data?.tkmDelta.value ?? 0}
					measure={data?.tkmDelta.measure ?? "-"}
					isLoading={isLoading}
				/>
			</Stack>

			<DoubleHorizontalBarChart
				data={chartData}
				title={t("root cause results/transported weight and mileage/graph title")}
				increaseLabel={t("root cause results/transported weight and mileage/increase")}
				decreaseLabel={t("root cause results/transported weight and mileage/decrease")}
				measure={t("root cause results/transported weight and mileage/legend label")}
				height={220}
				isLoading={isLoading}
				axisPaddingRatio={0.025}
			/>
		</Stack>
	);
};
